import { checkBool } from '../../universal-components/src/utils.js';
import { DEV_ENV, PROD_ENV, UAT_ENV } from './constants/environments.js';
import { subscribe, events, emit } from './eventManager.js';
import { COOKIE_DEFAULT_PATH, COOKIE_SAMESITE_STRICT, deleteCookie, getCookieDomain, getCookieValue, setCookie } from './utils/cookies.js';
import { WEBKEY_UPDATE_PROFILE_URL, WEBKEY_UPDATE_PROFILE_URL_DEV, getMiddlewareAPIURL } from './constants/api_urls.js';
import NavigationService from './NavigationService.js';
/**
 * Name of the storage key for storing the cart ID. Cart ID is currently stored in cookies, so it represents the cookie name.
 */
const CART_STORAGE_KEY_SUFFIX = 'cartId';
/** Annual subscription term identifier used in pricing options. */
export const ANNUAL_SUBSCRIPTION_TERM = 'Yearly';
/** Annual subscription display term shown in the UI. */
export const ANNUAL_SUBSCRIPTION_DISPLAY_TERM = 'Annual Subscription';
/** Monthly subscription term identifier used in pricing options. */
export const MONTHLY_SUBSCRIPTION_TERM = 'Monthly';
/** Monthly subscription display term shown in the UI. */
export const MONTHLY_SUBSCRIPTION_DISPLAY_TERM = 'Monthly Subscription';
/** 3-year subscription display term shown in the UI. */
export const THREE_YEAR_SUBSCRIPTION_DISPLAY_TERM = '3 Year Subscription';
/** 3-month trial display term shown in the UI. */
export const THREE_MOTHS_TRIAL_DISPLAY_TERM = '3 Month Trial';
// Query Parameters which control some views
const FULL_CART_QUERY_PARAM = 'fullCart';
const CART_COOKIE_MAX_AGE = 2592000; // 30 days
/**
 * Error code for duplicate subscription
 */
const SAME_PRODUCT_DIFFERENT_TERM = 'SAME_PRODUCT_DIFFERENT_TERM';
/**
 * Shopping cart environment which is used to inform the cart utility which API environment should be used.
 */
let cartEnv = PROD_ENV;
/**
 * The value which determines if the cart's onsite checkout is enabled.
 */
let checkoutEnabled = false;
/**
 * Represents the shopping cart cookie domain. Cookies are used to transfer carts across sites using top-level domain
 * cookies. Note that the cookies are also set per environment via the domain on the cookie.
 */
let cartCookieDomain;
/**
 * Checkout service API URL (potentially updated in the cart initialization).
 */
let checkoutServiceURL = `${getMiddlewareAPIURL(PROD_ENV)}/checkout`;
/**
 * Webkey update profile URL presented to users if they have an unsupported webkey country.
 */
let webkeyUpdateProfileURL = WEBKEY_UPDATE_PROFILE_URL;
/**
 * Represents the potential errors emitted by the cart module.
 */
const CART_EVENT_ERRORS = {
    CART_ERROR_INITIALIZING_CART: 'initializingCart',
    CART_ERROR_FETCHING_CART_DATA: 'fetchingCartData',
    CART_CLEARED_AFTER_CHECKOUT: 'cartClearedAfterCheckout',
};
/**
 * Represents the potential eCommerce events emitted by the cart module.
 */
const ECOMMERCE_EVENTS = {
    ECOMMERCE_PRODUCT_FETCHED: 'productFetched',
};
/**
 * Initializes cart infrastructure (environment, cookies, checkout URL).
 * Actual cart operations are handled by @disw/ecommerce.
 *
 * @param {CartProps} config the configuration for the cart module
 */
export async function initializeCart(config = {}) {
    const { env = PROD_ENV, checkout } = config;
    cartEnv = env;
    if (checkout) {
        checkoutEnabled = checkBool(checkout.enabled, false);
    }
    // Get the domain to use for any cookies utilized for the cart
    cartCookieDomain = getCookieDomain([DEV_ENV, UAT_ENV].includes(cartEnv));
    // Set the Checkout Service URL according to the configured environment
    checkoutServiceURL = `${getMiddlewareAPIURL(cartEnv)}/checkout`;
    // Set the webkeyUpdateProfileURL based on environment
    if (cartEnv === DEV_ENV) {
        webkeyUpdateProfileURL = WEBKEY_UPDATE_PROFILE_URL_DEV;
    }
    else if (cartEnv === UAT_ENV) {
        webkeyUpdateProfileURL = WEBKEY_UPDATE_PROFILE_URL;
    }
    else {
        webkeyUpdateProfileURL = WEBKEY_UPDATE_PROFILE_URL;
    }
}
/**
 * Emits a cart-related event with the provided parameters.
 *
 * @param {...any[]} params - The parameters to pass along with the cart event.
 * @returns {void}
 */
function emitCartEvent(...params) {
    emit(events.CART, ...params);
}
/**
 * Emits an ecommerce-related event with the provided parameters.
 *
 * @param {...any[]} params - The parameters to pass to the ecommerce event emitter.
 * @returns {void}
 */
function emitEcommerceEvent(...params) {
    emit(events.ECOMMERCE, ...params);
}
/**
 * Creates/Updates a cookie to store the cartId
 */
const _setCartCookie = (key, value) => {
    setCookie(key, value, {
        maxAge: CART_COOKIE_MAX_AGE,
        domain: cartCookieDomain,
        path: COOKIE_DEFAULT_PATH,
        sameSite: COOKIE_SAMESITE_STRICT,
    });
};
/**
 * Get the cartId from the cookie
 *
 * @param {string} key
 * @returns {string|undefined} Cookie value or undefined if it doesn't exist
 */
const _getCartCookieValue = (key) => {
    return getCookieValue(key);
};
/**
 * Delete a cart cookie
 */
const _deleteCartCookie = (key) => {
    deleteCookie(key, { domain: cartCookieDomain, path: COOKIE_DEFAULT_PATH });
};
/**
 * Function to subscribe to any changes in the cart.
 *
 * @param {CallbackFunction} callback
 * @return {function} function used to unsubscribe from the cart events
 */
function subscribeToCart(callback) {
    return subscribe(events.CART, callback);
}
/**
 * Function to subscribe to any eCommerce events which are emitted (product fetches, etc).
 *
 * @param {CallbackFunction} callback executed when the event is emitted.
 */
function subscribeToECommerce(callback) {
    return subscribe(events.ECOMMERCE, callback);
}
function subscribeToMiniCart(callback) {
    return subscribe(events.MINI_CART, callback);
}
function toggleMiniCart(state) {
    emit(events.MINI_CART, state);
}
function subscribeToFullCart(callback) {
    return subscribe(events.FULL_CART, callback);
}
/**
 * Changes fullcart open state — navigates to the /cart page.
 *
 * @param {boolean} state if the cart should be open or not
 */
async function toggleFullCart(state) {
    const pathname = NavigationService.getPathname();
    // Check if we're not already on the cart page before redirecting
    if (!pathname.includes('/cart')) {
        const currentLocaleCartPath = pathname.replace(/\/([a-z]{2}-([a-z]{2}|[A-Z]{2}))\/(.*)/, '/$1/cart');
        NavigationService.navigate(NavigationService.getOrigin() + currentLocaleCartPath);
    }
}
/**
 * Update the cart data by firing the `events.CART` event with the new cart data.
 *
 * @param newCartData The new data for the cart
 */
function updateCartData(newCartData) {
    emit(events.CART, newCartData, false);
}
/**
 * Clear the cart data after checkout is complete.
 */
function clearCartDataAfterCheckout() {
    const storageItemKey = `${window.disw.country.getCountry().countrycode}_${CART_STORAGE_KEY_SUFFIX}`;
    _deleteCartCookie(storageItemKey);
    emit(events.CART, null, CART_EVENT_ERRORS.CART_CLEARED_AFTER_CHECKOUT);
}
/**
 * Returns the URL for the Checkout Service Flow.
 *
 * @return {string} the base URL for the Checkout Service
 */
function getCartCheckoutServiceUrl() {
    return checkoutServiceURL;
}
/**
 * Return the URL for the webkey profile update page.
 *
 * @return {string} representing the URL for the webkey update profile page.
 */
function getWebkeyUpdateProfileUrl() {
    return webkeyUpdateProfileURL;
}
function isCheckoutEnabled() {
    return checkoutEnabled;
}
/**
 * Get the configured cart environment.
 */
function getCartEnv() {
    return cartEnv;
}
/**
 * Set the cart environment. This is used for testing purposes.
 *
 * @param env the environment
 */
function _setCartEnv(env) {
    cartEnv = env;
}
export default {
    ANNUAL_SUBSCRIPTION_TERM,
    ANNUAL_SUBSCRIPTION_DISPLAY_TERM,
    SAME_PRODUCT_DIFFERENT_TERM,
    MONTHLY_SUBSCRIPTION_TERM,
    MONTHLY_SUBSCRIPTION_DISPLAY_TERM,
    THREE_YEAR_SUBSCRIPTION_DISPLAY_TERM,
    clearCartDataAfterCheckout,
    getCartCheckoutServiceUrl,
    getWebkeyUpdateProfileUrl,
    isCheckoutEnabled,
    subscribeToCart,
    subscribeToECommerce,
    subscribeToFullCart,
    subscribeToMiniCart,
    toggleFullCart,
    toggleMiniCart,
    updateCartData,
    CART_ERROR_INITIALIZING_CART: CART_EVENT_ERRORS.CART_ERROR_INITIALIZING_CART,
    CART_ERROR_FETCHING_CART_DATA: CART_EVENT_ERRORS.CART_ERROR_FETCHING_CART_DATA,
    ECOMMERCE_PRODUCT_FETCHED: ECOMMERCE_EVENTS.ECOMMERCE_PRODUCT_FETCHED,
    CART_CLEARED_AFTER_CHECKOUT: CART_EVENT_ERRORS.CART_CLEARED_AFTER_CHECKOUT,
    getCartEnv,
    _setCartEnv,
    emitCartEvent,
    emitEcommerceEvent,
};
